import {useEffect, type RefObject} from 'react'

// 101 thresholds from 0.00 to 1.00 in 1% increments so the observer
// fires whenever the footer's visible fraction changes by ~1%.
const THRESHOLDS = Array.from({length: 101}, (_, i) => i / 100)

/**
 * Tracks how much of the page footer (`.footer`) is visible in the viewport
 * and exposes it as a `--footer-visible-height` CSS custom property on the
 * container element. Sticky panels inside the container can subtract this
 * value from their max-height to stay fully visible as the footer scrolls
 * into view.
 *
 * Uses IntersectionObserver with fine-grained thresholds for efficient,
 * browser-scheduled updates without scroll listeners.
 */
export function useAdjustStickyHeadersForFooter(containerRef: RefObject<HTMLElement | null>, enabled: boolean) {
  useEffect(() => {
    if (!enabled) return
    const footer: HTMLElement | null = document.querySelector('.footer')
    const container = containerRef.current
    if (!footer || !container) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return
        const visibleHeight = entry.isIntersecting ? Math.round(entry.intersectionRect.height) : 0
        container.style.setProperty('--footer-visible-height', `${visibleHeight}px`)
      },
      {threshold: THRESHOLDS},
    )

    observer.observe(footer)

    return () => {
      observer.disconnect()
      container.style.removeProperty('--footer-visible-height')
    }
  }, [enabled, containerRef])
}
